import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import {
  TITLES_DEFAULT_PARAMS,
  queryOptionsGetTitlesPaginated,
} from '../api/titles/titlesApi';
import { SEARCH_KEYS } from '../constant/SearchKeys';
import { useResetPageOnSearchChange } from '../hooks/useResetPageOnSearchChange';
import { isLoadingState } from '../utils/helpers/common';
import AppSearchField from './AppSearchField';
import AppTableV2, { AppTableV2HeaderCell as TH } from './AppTableV2';
import { ServiceBadge } from './Badges/ServiceBadge';
import { SingleQueryResults } from './QueryResults/SingleQueryResults';

interface ITitlesTableProps {
  isInDashboard?: boolean;
  isStaffView?: boolean;
  workspaceId?: string;
}

interface ISearchParams {
  sortBy?: string;
  titlesSearch?: string;
  titlesTablePage?: string;
}

/**
 * @deprecated New Titles page at /src/pages/v3/Title/TitlesV3.tsx
 * doesn't use daisy UI components or a table.
 */
const TitlesTable = ({
  isInDashboard,
  isStaffView,
  workspaceId,
}: ITitlesTableProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const typedSearchParams = Object.fromEntries(
    searchParams.entries(),
  ) as ISearchParams;

  // Table state
  const [page, setPage] = useState<number>(
    Number(typedSearchParams.titlesTablePage) || TITLES_DEFAULT_PARAMS.page,
  );
  const [sortBy, setSortBy] = useState<string>(
    typedSearchParams.sortBy || TITLES_DEFAULT_PARAMS.sortBy,
  );
  const [titlesSearch, setTitlesSearch] = useState<string>(
    typedSearchParams.titlesSearch || '',
  );

  useResetPageOnSearchChange(titlesSearch, 'titlesTablePage', setPage);

  // Fetch Titles
  const titlesQuery = useQuery(
    queryOptionsGetTitlesPaginated({
      name: titlesSearch,
      page,
      sortBy,
      workspaceId,
    }),
  );

  const { isLoading, isPlaceholderData } = titlesQuery;

  const handlePageRequest = (
    page: number,
    sortBy: string = TITLES_DEFAULT_PARAMS.sortBy,
  ) => {
    setPage(page);
    setSortBy(sortBy);
  };

  return (
    <SingleQueryResults query={titlesQuery}>
      {({ items: titles, meta: paginationMeta }) => (
        <AppTableV2
          id="titles-table"
          customEmptyTable={{
            description: !isInDashboard
              ? "Looks like you don't have any titles yet."
              : 'There are currently no titles that match your search criteria.',
            icon: 'filmScript',
            title: 'No titles found',
          }}
          filterElement={
            <AppSearchField
              loading={isPlaceholderData}
              placeHolder="Search Titles"
              searchKey={SEARCH_KEYS.TITLES_SEARCH}
              width="w-96"
              onSearchChange={(v) => setTitlesSearch(v)}
            />
          }
          hasBottomNavigation={true}
          headerRowElement={
            <tr>
              <TH
                className={clsx({
                  ['xs:!w-56 md:!w-[320px] xl:!w-[450px]']: isInDashboard,
                })}
                sortByProperty={isStaffView ? undefined : 'title'}
                sortOrderByProperty="desc"
              >
                Title
              </TH>
              <TH className={clsx({ ['hidden']: isInDashboard })}>Services</TH>
              <TH
                className={twMerge(
                  clsx({
                    ['xs:!w-56 md:!w-[320px] xl:!w-[450px]']: isInDashboard,
                  }),
                  clsx({ ['hidden']: !isInDashboard }),
                )}
              >
                Workspace
              </TH>
              <TH
                className={twMerge(
                  '!w-48',
                  clsx({ ['hidden']: isInDashboard }),
                )}
              >
                Type
              </TH>
              <TH
                className={twMerge(
                  '!w-28',
                  clsx({ ['hidden']: isInDashboard }),
                )}
              >
                Year
              </TH>
            </tr>
          }
          isLoading={isLoadingState(isLoading ? 'loading' : 'idle')}
          paginationKey="titlesTablePage"
          paginationMeta={paginationMeta}
          onPageRequest={handlePageRequest}
        >
          {titles?.map(
            ({
              _id: titleId,
              ownerWorkspaceId,
              ownerWorkspaceName,
              serviceType,
              title,
              titleType,
              year,
            }) => (
              <tr key={titleId}>
                <td
                  className={twMerge(
                    'text-wrap break-all',
                    clsx({
                      ['xs:!w-56 md:!w-[320px] xl:!w-[450px]']: isInDashboard,
                    }),
                  )}
                >
                  {isInDashboard || isStaffView ? (
                    <Link
                      className="font-sans text-base font-normal leading-normal text-primary hover:underline"
                      to={`/staff/workspaces/manage-workspaces/${ownerWorkspaceId}/summary/${titleId}`}
                    >
                      {title}
                    </Link>
                  ) : (
                    <Link
                      className="font-sans text-base font-normal leading-normal text-primary hover:underline"
                      to={`summary/${titleId}/payments`}
                    >
                      {title}
                    </Link>
                  )}
                </td>
                <td
                  className={twMerge(
                    'flex !w-fit items-center gap-2.5',
                    clsx({
                      ['hidden']: isInDashboard,
                    }),
                  )}
                >
                  {serviceType?.map(
                    (type, index) =>
                      type && (
                        <ServiceBadge
                          key={`${type}-${index}`}
                          serviceType={type}
                        />
                      ),
                  )}
                </td>
                <td
                  className={twMerge(
                    'text-wrap',
                    clsx({
                      ['hidden']: !isInDashboard,
                    }),
                  )}
                >
                  <Link
                    className="font-sans text-base font-normal leading-normal text-primary hover:underline"
                    to={`/staff/workspaces/manage-workspaces/${ownerWorkspaceId}`}
                  >
                    {ownerWorkspaceName}
                  </Link>
                </td>
                <td
                  className={twMerge(
                    clsx({
                      ['hidden']: isInDashboard,
                    }),
                  )}
                >
                  {titleType}
                </td>
                <td
                  className={twMerge(
                    clsx({
                      ['hidden']: isInDashboard,
                    }),
                  )}
                >
                  {year}
                </td>
              </tr>
            ),
          )}
        </AppTableV2>
      )}
    </SingleQueryResults>
  );
};

export default TitlesTable;
